/**
 * <MobileBottomNav> — thumb-reachable bottom navigation for the manager app.
 *
 * Visible only on mobile/tablet (`lg:hidden`). Desktop continues to use the
 * existing sidebar. Five slots:
 *
 *   [ Home ]  [ Tenants ]  [ + Add (elevated FAB) ]  [ Money ]  [ More ]
 *
 * Design choices:
 *  - iOS safe-area-inset-bottom respected so the bar doesn't sit under the
 *    home indicator on notched iPhones.
 *  - Center FAB is intentionally elevated +12px above the bar with a ring,
 *    matching native-app conventions (Instagram, Cash App, Splitwise).
 *  - Active state uses solid-color icon + label dot + scale animation so it
 *    feels tactile, not laggy.
 *  - "More" opens a vaul drawer with the full nav tree, scrollable.
 *  - "+ Add" opens a quick-action sheet — the most common day-to-day actions
 *    (record payment, add tenant, log expense, create lead) one tap deep.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  Users,
  IndianRupee,
  Plus,
  Menu as MenuIcon,
  X,
  // Quick-add icons
  Receipt,
  UserPlus,
  CreditCard,
  Tag,
  // More-menu icons
  LayoutDashboard,
  Building2,
  ListTodo,
  MessageCircle,
  Megaphone,
  UserCheck,
  UtensilsCrossed,
  Sparkle,
  Package,
  Store,
  FileText,
  FolderOpen,
  Zap,
  Wallet,
  Settings,
  LogOut,
  Bell,
} from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '../ui/drawer';
import { useAuth } from '../../context/AuthContext';
import { prefetch } from '../../services/prefetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// URLs each primary slot's destination page hits first on mount. Touch-start
// on a slot triggers these so the response is warm by the time we navigate.
// Keep this list small — prefetching too much is just wasted bandwidth.
const PREFETCH_URLS = {
  home:    [`${API}/analytics/home-summary`, `${API}/analytics/dashboard`],
  tenants: [`${API}/tenants`],
  money:   [`${API}/money/payments`, `${API}/tenants`],
};

const PRIMARY_ITEMS = [
  { key: 'home',    label: 'Home',    icon: HomeIcon,     path: '/manager/home' },
  { key: 'tenants', label: 'Tenants', icon: Users,        path: '/manager/people/tenants' },
  // index 2 = the center FAB, rendered separately
  { key: 'money',   label: 'Money',   icon: IndianRupee,  path: '/manager/money/collections' },
];

// "Quick add" sheet — the 4 actions a PG owner does daily.
const QUICK_ACTIONS = [
  {
    key: 'payment',
    label: 'Record Payment',
    sub: 'Log rent / deposit collection',
    icon: Receipt,
    accent: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    path: '/manager/money/collections?action=new',
  },
  {
    key: 'tenant',
    label: 'Add Tenant',
    sub: 'New move-in',
    icon: UserPlus,
    accent: 'bg-blue-50 text-blue-700 ring-blue-200',
    path: '/manager/people/tenants?action=new',
  },
  {
    key: 'expense',
    label: 'Add Expense',
    sub: 'Maintenance, utilities, food cost',
    icon: CreditCard,
    accent: 'bg-amber-50 text-amber-700 ring-amber-200',
    path: '/manager/money/expenses?action=new',
  },
  {
    key: 'lead',
    label: 'Add Lead',
    sub: 'New enquiry',
    icon: Tag,
    accent: 'bg-purple-50 text-purple-700 ring-purple-200',
    path: '/manager/people/leads?action=new',
  },
];

// More-menu groups (subset of the full sidebar — drawer is scrollable).
const MORE_GROUPS = [
  {
    title: 'Operations',
    items: [
      { label: 'Dashboard',    path: '/manager/dashboard',         icon: LayoutDashboard },
      { label: 'Property',     path: '/manager/property/list',     icon: Building2 },
      { label: 'Tasks',        path: '/manager/tasks',             icon: ListTodo },
      { label: 'Complaints',   path: '/manager/complaints',        icon: MessageCircle },
      { label: 'Notice Board', path: '/manager/notice-board',      icon: Megaphone },
      { label: 'Visitor Log',  path: '/manager/visitors',          icon: UserCheck },
    ],
  },
  {
    title: 'Services',
    items: [
      { label: 'Food / Mess', path: '/manager/food',     icon: UtensilsCrossed },
      { label: 'Cleaning',    path: '/manager/cleaning', icon: Sparkle },
      { label: 'Assets',      path: '/manager/assets',   icon: Package },
      { label: 'Listings',    path: '/manager/listings', icon: Store },
      { label: 'Electricity', path: '/manager/electricity', icon: Zap },
    ],
  },
  {
    title: 'Money & docs',
    items: [
      { label: 'Dues',          path: '/manager/money/dues',         icon: IndianRupee },
      { label: 'Invoices',      path: '/manager/invoices',           icon: Receipt },
      { label: 'Expenses',      path: '/manager/money/expenses',     icon: CreditCard },
      { label: 'Profit & Loss', path: '/manager/money/profit-loss',  icon: FileText },
      { label: 'Reports',       path: '/manager/reports',            icon: FileText },
      { label: 'Agreements',    path: '/manager/agreements',         icon: FileText },
      { label: 'Documents',     path: '/manager/documents',          icon: FolderOpen },
      { label: 'Wallet',        path: '/manager/wallet',             icon: Wallet },
    ],
  },
  {
    title: 'Account',
    items: [
      { label: 'Reminders',    path: '/manager/reminders',     icon: Bell },
      { label: 'WhatsApp',     path: '/manager/whatsapp',      icon: MessageCircle },
      { label: 'Settings',     path: '/manager/settings',      icon: Settings },
    ],
  },
];

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, token } = useAuth();
  const [quickOpen, setQuickOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Kick off the GETs each slot's destination page hits on mount. Called on
  // touchstart / mousedown / pointerenter so the cache is warm by the time
  // the user lifts their finger and we mount the new route. No-op if already
  // cached, AND no-op if we're already on the destination route (saves
  // bandwidth when the user idly taps the slot they're on).
  const warmFor = (key) => {
    if (!token) return;
    const slot = PRIMARY_ITEMS.find((i) => i.key === key);
    if (slot && location.pathname.startsWith(slot.path.split('?')[0])) return;
    const urls = PREFETCH_URLS[key];
    if (!urls) return;
    urls.forEach((u) => prefetch(u, token));
  };

  // A nav item is "active" when its path is the longest prefix match. We
  // sort primary items by descending path length to avoid false positives
  // (e.g. /manager/money matching /manager/money/collections).
  const isActive = (path) => {
    const matches = [...PRIMARY_ITEMS]
      .map((i) => i.path)
      .filter((p) => location.pathname.startsWith(p.split('?')[0]))
      .sort((a, b) => b.length - a.length);
    return matches[0] === path;
  };

  const go = (path) => {
    setQuickOpen(false);
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Spacer so page content can scroll past the bar without being hidden.
          Bar is ~76px tall + iOS home indicator; reserve 88px on mobile. */}
      <div aria-hidden className="lg:hidden h-[88px]" />

      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5 items-end h-[68px] px-2">
          {/* Slot 1 — Home */}
          <NavSlot
            item={PRIMARY_ITEMS[0]}
            active={isActive(PRIMARY_ITEMS[0].path)}
            onClick={() => go(PRIMARY_ITEMS[0].path)}
            onWarm={() => warmFor('home')}
          />
          {/* Slot 2 — Tenants */}
          <NavSlot
            item={PRIMARY_ITEMS[1]}
            active={isActive(PRIMARY_ITEMS[1].path)}
            onClick={() => go(PRIMARY_ITEMS[1].path)}
            onWarm={() => warmFor('tenants')}
          />

          {/* Slot 3 — Center FAB (Add) */}
          <button
            type="button"
            onClick={() => setQuickOpen(true)}
            className="relative flex flex-col items-center justify-end h-full focus:outline-none"
            data-testid="bottom-nav-add"
            aria-label="Quick add"
          >
            <span className="absolute -top-4 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg ring-4 ring-white flex items-center justify-center active:scale-95 transition-transform">
              <Plus size={26} strokeWidth={2.6} />
            </span>
            <span className="text-[10px] font-semibold text-emerald-700 mt-1 mb-1">Add</span>
          </button>

          {/* Slot 4 — Money */}
          <NavSlot
            item={PRIMARY_ITEMS[2]}
            active={isActive(PRIMARY_ITEMS[2].path)}
            onClick={() => go(PRIMARY_ITEMS[2].path)}
            onWarm={() => warmFor('money')}
          />
          {/* Slot 5 — More */}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center h-full gap-0.5 active:scale-95 transition-transform focus:outline-none"
            data-testid="bottom-nav-more"
            aria-label="More options"
          >
            <MenuIcon size={20} className="text-gray-500" />
            <span className="text-[10px] font-medium text-gray-600">More</span>
          </button>
        </div>
      </nav>

      {/* Quick-add action sheet */}
      <Drawer open={quickOpen} onOpenChange={setQuickOpen}>
        <DrawerContent data-testid="bottom-nav-quickadd-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle>Quick add</DrawerTitle>
            <DrawerDescription>One-tap to the action you do most.</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-2">
            {QUICK_ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.key}
                  onClick={() => go(a.path)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl ring-1 ring-gray-200 hover:bg-gray-50 active:scale-[0.98] transition-all text-left"
                  data-testid={`quickadd-${a.key}`}
                >
                  <span className={`w-11 h-11 rounded-xl ring-1 ${a.accent} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={20} />
                  </span>
                  <span className="flex-1">
                    <span className="block font-semibold text-gray-900 text-sm">{a.label}</span>
                    <span className="block text-xs text-gray-500">{a.sub}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>

      {/* More drawer — full nav tree, scrollable. */}
      <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
        <DrawerContent className="max-h-[85vh]" data-testid="bottom-nav-more-drawer">
          <DrawerHeader className="text-left flex items-center justify-between pr-4">
            <div>
              <DrawerTitle>All sections</DrawerTitle>
              <DrawerDescription>Everything that lives in the desktop sidebar.</DrawerDescription>
            </div>
            <button
              onClick={() => setMoreOpen(false)}
              aria-label="Close"
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <X size={18} />
            </button>
          </DrawerHeader>
          <div className="px-4 pb-6 overflow-y-auto">
            {MORE_GROUPS.map((g) => (
              <section key={g.title} className="mb-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                  {g.title}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    const active = location.pathname.startsWith(it.path);
                    return (
                      <button
                        key={it.path}
                        onClick={() => go(it.path)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl ring-1 active:scale-[0.97] transition-all text-left ${
                          active
                            ? 'ring-emerald-300 bg-emerald-50 text-emerald-800'
                            : 'ring-gray-200 hover:bg-gray-50 text-gray-700'
                        }`}
                        data-testid={`more-nav-${it.path.replace(/\//g, '_')}`}
                      >
                        <Icon size={16} className={active ? 'text-emerald-700' : 'text-gray-500'} />
                        <span className="text-sm font-medium truncate">{it.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Logout — destructive action separated visually */}
            <button
              onClick={() => {
                setMoreOpen(false);
                logout();
                navigate('/manager/login');
              }}
              className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-red-50 text-red-700 ring-1 ring-red-200 active:scale-[0.98] transition-all"
              data-testid="bottom-nav-logout"
            >
              <LogOut size={16} />
              <span className="font-semibold text-sm">Sign out</span>
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// Single bottom-nav slot — icon + label with active state animation.
//
// `onWarm` is invoked on the EARLIEST UI signal that the user intends to
// tap this slot. We listen on three events for maximum cross-browser cover:
//   • onTouchStart    — fires on the moment a finger touches the screen
//                        (iOS Safari + modern Android Chrome)
//   • onPointerDown   — unified pointer model, fires on touch + mouse + stylus
//   • onPointerEnter  — desktop/iPad-with-mouse hover (~200ms before click)
// All three are idempotent thanks to the cache's TTL-window dedupe.
const NavSlot = ({ item, active, onClick, onWarm }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      onTouchStart={onWarm}
      onPointerDown={onWarm}
      onPointerEnter={onWarm}
      className="flex flex-col items-center justify-center h-full gap-0.5 active:scale-95 transition-transform focus:outline-none"
      data-testid={`bottom-nav-${item.key}`}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
    >
      <span
        className={`flex items-center justify-center w-9 h-9 rounded-xl transition-colors ${
          active ? 'bg-emerald-50 text-emerald-700' : 'text-gray-500'
        }`}
      >
        <Icon size={20} strokeWidth={active ? 2.4 : 2} />
      </span>
      <span
        className={`text-[10px] font-medium transition-colors ${
          active ? 'text-emerald-700' : 'text-gray-600'
        }`}
      >
        {item.label}
      </span>
    </button>
  );
};

export default MobileBottomNav;
